import callApi from '../../util/apiCaller';
import { flat, unique } from '../../util/array';

import { getSubject } from '../Subject/SubjectActions';
import { checkVariables } from '../Subject/VariableActions';
import { formatPrice } from '../../util/price';

// Export Constants
export const SET_QUESTIONS = 'SET_QUESTIONS';

export const QUESTION_ASSOCIATION_SIDES = ['left', 'right'];
export const QUESTION_FILE_ALLOWED_FILE_COUNT = 5;
export const QUESTION_AUDIO_ALLOWED_RECORDING_COUNT = 1;
export const QUESTION_AUDIO_MAX_RECORDING_COUNT = 10;

export const QUESTION_FILL_COMPONENT_REGEX = /\[(\d+)\]/;
export const QUESTION_FILL_COMPONENT_REGEX_GLOBAL = /\[(\d+)\]/g;

export function getQuestionsRequest(subjectId) {
    return dispatch => {
        return callApi(`questions/${subjectId}`).then(result => {
            dispatch(setQuestions(subjectId, result.questions));
            return result.questions;
        }).catch(err => {
            return null;
        });
    };
}

export function editQuestionRequest(question, requestAll = true) {
    return dispatch => {
        return callApi('question/edit', 'post', { question }).then(result => {
            requestAll && result.question && dispatch(getQuestionsRequest(result.question.subject));
            return result.question;
        }).catch(err => {
            return null;
        });
    };
}

export function sortQuestionsRequest(subjectId, questions) {
    return dispatch => {
        return callApi('question/sort', 'post', { subject: subjectId, questions: questions.map(question => ({ _id: question._id, sort: question.sort })) }).then(result => {
            return result.ok && dispatch(getQuestionsRequest(questions[0].subject));
        }).catch(err => {
            return null;
        });
    };
}

export function searchQuestionsRequest(search) {
    return dispatch => {
        return callApi(`question/search/${search}`).then(result => {
            return result.questions;
        }).catch(err => {
            return null;
        });
    };
}

export function removeQuestionRequest(question) {
    return dispatch => {
        return callApi('question/remove', 'delete', { question: { _id: question._id } }).then(result => {
            dispatch(getQuestionsRequest(question.subject));
            return result.ok;
        }).catch(err => {
            return null;
        });
    };
}

// Getters
export function getQuestions(store, subjectId, pageId = null) {
    const subject = getSubject(store, subjectId);
    let questions = (subject && subject.questions) || [];
    if(pageId !== null) {
        questions = questions.filter(question => question.page === pageId);
    }
    return questions;
}

export function getQuestion(store, subjectId, id) {
    return getQuestions(store, subjectId).find(question => question._id === id);
}

export function getQuestionTypes() {
    return ['closed', 'open', 'qcm', 'fill', 'association', 'file', 'audio', 'external'];
}

export function getQuestionOption(question, option) {
    if(question && question.options) {
        return question.options[option];
    }
    return '';
}

export function getQuestionTypeColor(type) {
    switch(type) {
        case 'closed':
            return 'primary';

        case 'open':
            return 'info';

        case 'qcm':
            return 'success';

        case 'fill':
            return 'dark';

        case 'association':
            return 'warning';

        case 'file':
            return 'secondary';

        case 'audio':
            return 'info';

        case 'external':
        default:
            return 'light';
    }
}

export function getAvailableFileTypes() {
    return ['text', 'image', 'audio', 'video'];
}

export function getQuestionsLearningGoals(store, subjectId) {
    let lgs = (getSubject(store, subjectId).learningGoals || '').split(',');
    getQuestions(store, subjectId).forEach(question => {
        lgs = lgs.concat(question.learningGoals);
    });
    return unique(lgs);
}

export function getQuestionLearningGoals(question) {
    return flat((question.learningGoals || []).concat((question.subNotation || []).map(subNotation => subNotation.learningGoal.split(','))));
}

export function checkQuestionVariables(subject, question) {
    // Choices can be nested (fill) and hold objects: only their texts carry formulas
    const choicesTexts = flat((question.choices || []).map(choice => (Array.isArray(choice) ? choice : [choice])))
        .map(choice => getChoiceDisplay(choice));
    return checkVariables([question.label, question.content, ...question.answers, ...choicesTexts], subject.variables);
}

export function hasContentToFill(question) {
    return QUESTION_FILL_COMPONENT_REGEX.test(`${question.label || ''} ${question.content || ''}`);
}

export function getContentToFill(question) {
    return `${question.label || ''} ${(question.content || '')}`.match(QUESTION_FILL_COMPONENT_REGEX_GLOBAL) || [];
}

export function getFillSpaces(question) {
    const fillSpaces = [];

    const regex1 = /\[(\d+)\]/g;
    const str1 = `${question.label || ''} ${question.content || ''}`;
    let array1;

    while((array1 = regex1.exec(str1)) !== null) {
        array1[1] && !fillSpaces.includes(parseInt(array1[1], 10)) && fillSpaces.push(parseInt(array1[1], 10));
    }

    return fillSpaces.map(marker => parseInt(marker, 10)).sort((a, b) => a - b);
}

export function getFillSpaceChoices(question, fillIndex) {
    const fillSpaceChoices = (question.choices || [])[fillIndex] || [];
    if(!Array.isArray(fillSpaceChoices)) {
        return [fillSpaceChoices];
    }
    return fillSpaceChoices;
}

/*
 * A choice is either a legacy string or an object { display, value }
 * The display is what the applicant reads, the value is what is stored in answers
 */
export function getChoiceDisplay(choice) {
    if(choice && typeof choice === 'object') {
        return `${choice.display || ''}`;
    }
    return `${choice || ''}`;
}

export function getChoiceValue(choice) {
    if(choice && typeof choice === 'object') {
        return `${choice.value || choice.display || ''}`;
    }
    return `${choice || ''}`;
}

export function getTotalPoints(questions) {
    return formatPrice((questions || []).filter(question => question.isActive).reduce((total, question) => {
        return total + parseFloat(`${question.points}`.replace(',', '.'));
    }, 0));
}

// Export Actions
export function setQuestions(subjectId, questions) {
    return {
        type: SET_QUESTIONS,
        subjectId,
        questions,
    };
}
